import { useEffect, useRef } from "react"

import styled, { css } from "styled-components"
import { showNotification, NOTIFICATION_TYPES } from "features/common/notifications"
import { userActions } from "features/user/model/slice/userSlice"
import { Avatar, Upload, Button, Spin, UserOutlined } from "shared/ui"
import { useAppDispatch } from "shared/config/store"

const ACCEPTED_FORMATS = ["image/jpeg", "image/png", "image/jpg"]
const ACCEPTED_EXTENSIONS = [".jpeg", ".jpg", ".png"]



const AvatarSpinner = ({ loading }: { loading?: boolean }) => {
  if (!loading) return null
  
  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255, 255, 255, 0.8)',
      borderRadius: '50%'
    }}
    >
      <Spin size="small" />
    </div>
  )
}

export const ProfileAvatar = ({
  loading,
  avatarUrl = null,
  localAvatarUrl = null,
  onBeforeUpload,
  onDelete,
}: {
  loading?: boolean;
  avatarUrl?: string;
  localAvatarUrl?: string;
  onUpload: (file: File) => void;
  onBeforeUpload: (file: File) => void;
  onDelete: () => void;
}) => {
  const revokeUrlRef = useRef<string | null>(null)
  const dispatch = useAppDispatch()

  useEffect(() => {
    return () => {
      if (revokeUrlRef.current) {
        URL.revokeObjectURL(revokeUrlRef.current)
      }
    }
  }, [])

  const finalAvatarUrl = localAvatarUrl || avatarUrl || null

  const isAcceptedFile = (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const mime = file.type.toLowerCase()
    return (
      ACCEPTED_FORMATS.includes(mime) || ACCEPTED_EXTENSIONS.includes(`.${ext}`)
    )
  }

  const handleFile = async (file: File) => {
    if (!isAcceptedFile(file)) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Unsupported file format',
        message: 'Unsupported file format. Please use JPG, PNG, or HEIC.'
      })
      return
    }

    const imageFile = file
    // HEIC обработка (заглушка)
    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'HEIC not supported',
        message: 'HEIC images are not supported in this demo. Please use JPG or PNG.'
      })
      return
    }

    // Чтение файла как dataURL
    const reader = new FileReader()
    reader.onload = () => {
      const img = new window.Image()
      img.onload = () => {
        // Определяем квадратную область по центру
        const minSide = Math.min(img.width, img.height)
        const sx = (img.width - minSide) / 2
        const sy = (img.height - minSide) / 2
        // Создаём canvas для crop
        const canvas = document.createElement('canvas')
        canvas.width = 160
        canvas.height = 160
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          showNotification({
            type: NOTIFICATION_TYPES.ERROR,
            title: 'Canvas error',
            message: 'Failed to process image.'
          })
          return
        }
        // Обрезаем и ресайзим
        ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, 160, 160)
        // Получаем blob для отправки (или dataURL для превью)
        canvas.toBlob((blob) => {
          if (!blob) {
            showNotification({
              type: NOTIFICATION_TYPES.ERROR,
              title: 'Image error',
              message: 'Failed to process image.'
            })
            return
          }

          const processedFile = new File([blob], imageFile.name, { type: blob.type })
          onBeforeUpload(processedFile)
          const url = URL.createObjectURL(processedFile)
          dispatch(userActions.setLocalAvatarUrl(url))
          revokeUrlRef.current = url
        }, 'image/png', 0.95)
      }
      img.onerror = () => {
        showNotification({
          type: NOTIFICATION_TYPES.ERROR,
          title: 'Image error',
          message: 'Failed to load image.'
        })
      }
      img.src = reader.result as string
    }
    reader.onerror = () => {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'File error',
        message: 'Failed to read file.'
      })
    }
    reader.readAsDataURL(imageFile)
  }

  return (
    <StyledAvatarWrapper>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <StyledAvatar
            size={80}
            src={finalAvatarUrl || undefined}
            icon={!finalAvatarUrl ? <UserOutlined style={{ fontSize: 32, color: '#fff' }} /> : undefined}
            style={{ background: !finalAvatarUrl ? '#bdbdbd' : undefined, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          />

          <AvatarSpinner loading={loading} />
        </div>

        <AvatarHint>
          <div>
          JPG, PNG
          </div>
        </AvatarHint>
      </div>

      <StyledAvatarButtons>
        <Upload
          showUploadList={false}
          accept=".jpeg,.jpg,.png,image/jpeg,image/png,image/jpg"
          beforeUpload={file => {
            handleFile(file)
            return false
          }}
          disabled={loading}
        >
          <StyledUploadButton
            type="link"
            disabled={loading}
          >
            Upload new profile photo
          </StyledUploadButton>
        </Upload>

        {avatarUrl && (
          <StyledDeleteButton
            type="link"
            onClick={onDelete}
            disabled={loading}
            danger
          >
            Delete photo
          </StyledDeleteButton>
        )}
      </StyledAvatarButtons>
    </StyledAvatarWrapper>
  )
}

const AvatarHint = styled.div`
  font-size: 10px;
  color: #888;
  margin-top: 8px;
  margin-bottom: 4px;
  text-align: center;
`

const StyledAvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
  cursor: pointer;
`

const StyledAvatarButtons = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  position: relative;
  top: -21px;
`

const CommonButtonStyles = css`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  height: 32px;
  border-radius: 6px;
  padding: 0 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none !important;
  box-shadow: none !important;
`

const StyledUploadButton = styled(Button)`
  ${CommonButtonStyles}
  color: rgba(0,0,0,0.88) !important;
  &:hover, &:focus {
    color: rgba(0,0,0,1);
  }
`

const StyledDeleteButton = styled(Button)`
  ${CommonButtonStyles}
  color: rgba(0, 0, 0, 0.45) !important;
  &:hover, &:focus {
    color: rgba(0, 0, 0, 0.45) !important;
  }
`
